/**
 * Block User UseCase
 *
 * Handles blocking/unblocking users:
 * 1. Optimistically update SecureStorage (for instant UI feedback)
 * 2. Call userService to update via cloud function
 * 3. Revert SecureStorage if cloud function fails
 *
 * PARITY NOTE: this optimistic-before-server ordering is a deliberate web
 * divergence from Flutter. Flutter's `UsersRepository.blockUser`
 * (lib/data/repository/users_repository.dart:188-200) calls the Cloud Function
 * FIRST and writes the local `BlockedUser` box ONLY inside `if (success)` —
 * no optimistic write, no rollback. The web port writes local storage
 * optimistically before the server hop and rolls back on failure for instant
 * UI feedback.
 * This is an INTENTIONAL web-vs-Flutter divergence (not a Flutter bug): Flutter's
 * server-first ordering is correct and safe; the web deliberately trades it for
 * optimistic local writes + rollback to get instant UI feedback. No Flutter
 * follow-up is owed.
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { userService } from '@/domain/services/user/userService';
import type { BlockedUserStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { ChatUtils } from '@/domain/utils/chatUtils';
import { auth } from '@/data/services/firebase';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class BlockUserUseCase {
  private storage: SecureStorageWebCrypto;

  constructor(storage?: SecureStorageWebCrypto) {
    this.storage = storage || SecureStorageWebCrypto.getInstance();
  }

  /**
   * Toggle block state for a user (block/unblock)
   *
   * @param userId - The Firebase ID of the user to block/unblock
   * @returns Promise<boolean> - Success status
   */
  async execute(userId: string): Promise<boolean> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        appLogger.error('No authenticated user', 'BlockUserUseCase.execute');
        return false;
      }

      // Generate groupChatId like Flutter does
      const groupChatId = ChatUtils.getGroupChatId(currentUser.uid, userId);

      // Get current block state from storage
      const storageKey = SECURE_STORAGE_KEYS.BLOCKED_USERS + userId;
      const currentBlockedUser = await this.storage.getEncrypted<BlockedUserStorage>(storageKey);

      // Determine new block state (toggle)
      const isCurrentlyBlocked = currentBlockedUser !== null;
      const shouldBlock = !isCurrentlyBlocked;

      // Step 1: Optimistically update local storage for instant UI feedback
      let newBlockedUser: BlockedUserStorage | null = null;

      if (shouldBlock) {
        newBlockedUser = {
          firebaseId: userId,
        };
        await this.storage.setEncrypted(storageKey, newBlockedUser);
      } else {
        await this.storage.remove(storageKey);
      }

      // Step 2: Call userService to update via cloud function
      try {
        const success = await userService.blockUser(userId, groupChatId);

        if (!success) {
          throw new Error('Block user operation returned success: false');
        }
      } catch (cloudFunctionError) {
        // Step 3: Revert storage on failure
        appLogger.error('Cloud function error, reverting local storage', 'BlockUserUseCase.execute', { error: cloudFunctionError as Error });

        if (currentBlockedUser) {
          // Restore previous state
          await this.storage.setEncrypted(storageKey, currentBlockedUser);
        } else {
          // Remove the blocked user if it didn't exist before
          await this.storage.remove(storageKey);
        }

        return false;
      }

      return true;
    } catch (error) {
      appLogger.error('Error blocking user', 'BlockUserUseCase.execute', { error: error as Error });
      return false;
    }
  }
}

export const blockUserUseCase = new BlockUserUseCase();
